import React from 'react';
import Table from '@mui/material/Table';
import { useListContext } from '../../ListContext';
import Pagination from '../../Pagination/Pagination';
import TableHead, { TableHeadProps } from './TableHead';
import TableBody, { TableBodyProps } from './TableBody';
import { TableBodyWithGroupingProps } from './TableBodyWithGrouping';
import { UiThemeOverrider, useUiTheme } from 'framework/uiTheme';
import ListFieldLayout from '../../../ui/layout/ListFieldLayout/ListFieldLayout';
import TotalRecords from '../TotalRecords';
import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import ListLoadingDecorator from '../ListLoadingDecorator';
import Alert from '../../../ui/mui-builder/Alert/Alert';
import { listGeneralSharedSx, listGeneralTableSx, tableBaseSx } from '../dataGridSx';

interface DataGridLayoutProps extends TableHeadProps, TableBodyProps {
  isSmallTable?: boolean;
  tableWrapperClassName?: string;
  rowKeySource?: string;
  tableBodyComponent?: React.FC<TableBodyProps | TableBodyWithGroupingProps>;
  emptyListMessageComponent?: React.ReactNode;
  showTotal?: boolean;
  showPagination?: boolean;
  customEmptyMessage?: string;
  sxPaginationWrapper?: SystemStyleObject<Theme>;
  sxTableWrapper?: SystemStyleObject<Theme>;
  sxResponsiveWrapper?: SystemStyleObject<Theme>;
  isShowHeader?: boolean;

  isSmallLoader?: boolean;
  hasBackgroundLoader?: boolean;
  hasPositionFixedLoader?: boolean;
  isInitialLoaderPosition?: boolean;
}

const DataGridLayout = ({
  children,
  buttons,
  rowProps = {},
  isSmallTable = false,
  isSmallLoader = false,
  hasBackgroundLoader = false,
  hasPositionFixedLoader = false,
  isInitialLoaderPosition = false,
  tableWrapperClassName = '',
  rowKeySource,
  tableBodyComponent: TableBodyComponent = TableBody,
  tableRowComponent,
  emptyListMessageComponent = '',
  customEmptyMessage = '',
  showTotal = true,
  showPagination = true,
  sxPaginationWrapper = {},
  sxTableWrapper = {},
  sxResponsiveWrapper = {},
  isShowHeader = true,
}: DataGridLayoutProps) => {
  const { data: { items = [], totalElements } = {}, loading } = useListContext();
  const { Alert: ThemedAlert = Alert } = useUiTheme();

  const emptyListComponent = () => {
    if (emptyListMessageComponent) {
      return emptyListMessageComponent;
    }

    return (
      <ThemedAlert color="info" sx={{ mt: 1.5 }}>
        {customEmptyMessage || 'No records found'}
      </ThemedAlert>
    );
  };

  return (
    <ListLoadingDecorator
      hasBackground={hasBackgroundLoader}
      hasPositionFixed={hasPositionFixedLoader}
      isInitialPosition={isInitialLoaderPosition}
      items={items}
      loading={loading}
      isSmallLoader={isSmallLoader}
    >
      <UiThemeOverrider
        overriddenLayouts={{
          BaseFieldLayout: ListFieldLayout,
        }}
      >
        <Box
          className={isSmallTable ? 'list-general list-general--sm' : undefined}
          sx={{
            display: 'block',
            width: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            ...(isSmallTable ? listGeneralSharedSx : {}),
            ...sxResponsiveWrapper,
          }}
        >
          <Box className={tableWrapperClassName || undefined} sx={sxTableWrapper}>
            {items.length ? (
              <Table
                size={isSmallTable ? 'small' : 'medium'}
                className={!isSmallTable ? 'list-general' : undefined}
                sx={isSmallTable ? tableBaseSx : { ...tableBaseSx, ...listGeneralTableSx }}
              >
                <TableHead isShowHeader={isShowHeader} buttons={buttons}>
                  {children}
                </TableHead>
                <TableBodyComponent
                  rowProps={rowProps}
                  buttons={buttons}
                  rowKeySource={rowKeySource}
                  tableRowComponent={tableRowComponent}
                >
                  {children}
                </TableBodyComponent>
              </Table>
            ) : (
              emptyListComponent()
            )}
          </Box>
        </Box>
        {showPagination && !!totalElements && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: '1px',
              marginBottom: '10px',
              minHeight: '32px',
              paddingTop: '25px',
              '.MuiPagination-root': { marginRight: '20px' },
              ...sxPaginationWrapper,
            }}
          >
            <Pagination />
            {showTotal && <TotalRecords />}
          </Box>
        )}
      </UiThemeOverrider>
    </ListLoadingDecorator>
  );
};

export default DataGridLayout;
